import type {
  DocumentRowMapped,
  TimelineDateBasis,
  TimelineGranularity,
} from "@/stores/documents/repository";
// getDocTimestamp lives in the repository layer now — it reads a column, and
// the timeline's paging cursor needs the same one. Imported from its own module
// rather than the repository barrel: store tests routinely
// `vi.mock("../repository")` with a partial stub, and a helper reachable only
// through that barrel comes back undefined inside them. Consumers import it
// from there directly too — re-exporting an imported binding out of this module
// is what Vite's SSR transform turns into a ReferenceError.
import { getDocTimestamp } from "@/stores/documents/repository/documentDates";

// Server-side buckets are produced by Postgres `date_trunc(..., timestamptz)`, which
// truncates in UTC. The merge in DocumentStore keys buckets on `start.toISOString()`,
// so client-side bucket starts must be UTC-aligned too — otherwise an Estonia-local
// "Mar 1 00:00" maps to "2026-02-28T22:00:00Z" and never collides with the server's
// "2026-03-01T00:00:00Z" key, producing a duplicate Mar 2026 bucket (WHA-3179).
//
// The end is the NEXT bucket's start, i.e. exclusive — the server's
// `date_trunc(...) + interval` end, not a millisecond short of it. A bucket
// built here is expanded through the same window RPC (`p_end_time` is
// exclusive), and the rows spliced into it are selected with the same
// half-open test, so the two conventions have to be one.
function bucketBoundaries(ts: Date, granularity: TimelineGranularity): { start: Date; end: Date } {
  const y = ts.getUTCFullYear();
  const m = ts.getUTCMonth();
  const d = ts.getUTCDate();
  switch (granularity) {
    case "year":
      return {
        start: new Date(Date.UTC(y, 0, 1)),
        end: new Date(Date.UTC(y + 1, 0, 1)),
      };
    case "month":
      return {
        start: new Date(Date.UTC(y, m, 1)),
        end: new Date(Date.UTC(y, m + 1, 1)),
      };
    case "week": {
      // ISO week: Monday-based, UTC. getUTCDay returns 0=Sun..6=Sat;
      // shift so Monday = 0 days back, Sunday = 6 days back.
      const dayOfWeek = (ts.getUTCDay() + 6) % 7;
      const monday = new Date(Date.UTC(y, m, d - dayOfWeek));
      const nextMonday = new Date(Date.UTC(y, m, d - dayOfWeek + 7));
      return { start: monday, end: nextMonday };
    }
    case "day":
      return {
        start: new Date(Date.UTC(y, m, d)),
        end: new Date(Date.UTC(y, m, d + 1)),
      };
  }
}

export interface ContentMatchBucket {
  bucketStart: Date;
  bucketEnd: Date;
  docs: DocumentRowMapped[];
}

export function bucketContentMatchDocs(
  docs: DocumentRowMapped[],
  granularity: TimelineGranularity,
  basis: TimelineDateBasis = "file"
): Map<string, ContentMatchBucket> {
  const buckets = new Map<string, ContentMatchBucket>();
  for (const doc of docs) {
    const ts = getDocTimestamp(doc, basis);
    const { start, end } = bucketBoundaries(ts, granularity);
    const key = start.toISOString();
    const existing = buckets.get(key);
    if (existing) {
      existing.docs.push(doc);
    } else {
      buckets.set(key, { bucketStart: start, bucketEnd: end, docs: [doc] });
    }
  }
  return buckets;
}
